import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import {
  Modal,
  ModalContent,
  ModalOverlay
} from 'src/shared/components/common/modal/Modal'
import { useModal } from 'src/shared/components/common/modal'
import Upsell from '../Upsell'
import Button, { BrandedButton } from 'src/shared/components/common/button'
import { usePopoverContext } from 'src/shared/components/common/popover/PopoverContext'
import { ButtonType } from 'src/apollo/sites'
import { CheckCircleSelectedIcon } from '@toasttab/buffet-pui-icons'
import { CART_POPOVER_CONTEXT_KEY } from '../../cart/CartModal'
import { useUpsell } from 'src/public/components/online_ordering/UpsellContext'
import { t } from 'src/translations'
import ItemModal from 'src/public/components/default_template/online_ordering/item_modal/ItemModal'
import { ItemSource } from '../../cart/cartUtils'
import { UpsellItem } from 'public/components/default_template/online_ordering/upsell/UpsellItem'
import { ScreenWidth, useIsMobile } from 'shared/js/utils/WindowContext'
import { useRestaurantRoutes } from 'shared/components/common/restaurant_routes/RestaurantRoutesContext'
import { Channel } from 'public/js/siteUtilities'
import { useCart } from 'public/components/online_ordering/CartContext'
import { useA11y } from 'src/public/components/online_ordering/useA11y'

const UpsellModal = () => {
  const cartContext = usePopoverContext(CART_POPOVER_CONTEXT_KEY)
  const { channel } = useCart()
  const isMobile = useIsMobile(ScreenWidth.EXTRA_SMALL)
  const {
    upsells: upsellItems,
    views,
    maxUpsellModalViews,
    upsellModalOpen,
    closeUpsellModal,
    addedToCartQuantity,
    selectedUpsellItem,
    handleUpsellItemClicked
  } = useUpsell()

  const {
    isOpen: itemModalOpen,
    onClose: closeItemModal,
    onOpen: openItemModal
  } = useModal()

  const { announce } = useA11y()

  const { orderPath, ecommPath } = useRestaurantRoutes()
  const path = channel === Channel.ECOMM && ecommPath ? ecommPath : orderPath

  const handleCloseItemModal = useCallback(() => {
    closeItemModal()
    window.history.replaceState(null, '', path)
  }, [closeItemModal, path])

  const isModalOpen = useMemo(() => {
    return (
      upsellModalOpen && views <= maxUpsellModalViews && upsellItems.length > 0
    )
  }, [upsellModalOpen, views, maxUpsellModalViews, upsellItems.length])

  const announceTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    if (isModalOpen) {
      announceTimeoutRef.current = setTimeout(() => {
        announce(
          t('upsell-drawer.items-added', {
            quantity: addedToCartQuantity
          })
        )
      }, 100)
    }
    return () => {
      if (announceTimeoutRef.current) clearTimeout(announceTimeoutRef.current)
    }
  }, [isModalOpen, announce, addedToCartQuantity])

  return (
    <>
      <Modal
        isOpen={isModalOpen}
        onClose={closeUpsellModal}
        testId='upsell-drawer'
      >
        <ModalOverlay />
        <ModalContent contentClassName='modalContentV2 bottomDrawerMobile upsellDrawer'>
          <div className='modalContent'>
            <div className='topContent'>
              <h2 className='heading'>
                <CheckCircleSelectedIcon
                  size='sm'
                  accessibility='decorative'
                  className='icon'
                />
                <span className='type-default'>
                  {t('upsell-drawer.items-added', {
                    quantity: addedToCartQuantity
                  })}
                </span>
              </h2>
              <Button
                onClick={() => {
                  closeUpsellModal()
                  cartContext?.open()
                }}
                className='upsellButton viewCartButton'
                aria-haspopup='dialog'
              >
                {t('upsell-drawer.button.cart')}
              </Button>
              <Upsell
                heading={t('upsell-drawer.heading')}
                onItemSelect={(item: UpsellItem) => {
                  handleUpsellItemClicked(
                    item,
                    openItemModal,
                    ItemSource.UpsellModal
                  )
                  closeUpsellModal()
                  // When it's a quick-add item on desktop, there's no visual feedback that the
                  // item has been added since there's no FAB animation, so open the cart instead.
                  if (!item.hasModifiers && !isMobile) cartContext?.open()
                }}
              />
            </div>
            <div className='footer'>
              <BrandedButton
                variant={ButtonType.Primary}
                onClick={() => {
                  closeUpsellModal()
                  cartContext?.close()
                }}
                className='upsellButton'
              >
                {t('upsell-drawer.button.menu')}
              </BrandedButton>
            </div>
          </div>
        </ModalContent>
      </Modal>
      {selectedUpsellItem && (
        <ItemModal
          isOpen={itemModalOpen}
          onClose={handleCloseItemModal}
          itemGuid={selectedUpsellItem.itemGuid}
          itemGroupGuid={selectedUpsellItem.itemGroupGuid}
          itemSource={ItemSource.UpsellModal}
        />
      )}
    </>
  )
}

export default UpsellModal
